import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { apiGet } from '../api/client.js';

const VERDICT_STYLE = {
  hit:     { color: 'var(--color-amber-400)', label: 'Hit' },
  average: { color: 'var(--color-warn)',      label: 'Average' },
  miss:    { color: 'var(--color-queued)',     label: 'Miss' },
};

const PILLAR_LABELS = {
  pain_point:       'Pain Point',
  tip:              'Tip',
  product_showcase: 'Showcase',
  reel:             'Reel',
  story:            'Story',
};

function ScoreBar({ score }) {
  const pct = Math.max(0, Math.min(100, score || 0));
  const color = pct >= 70
    ? 'var(--color-amber-500)'
    : pct >= 45
      ? 'var(--color-warn)'
      : 'var(--color-queued)';
  return (
    <div className="score-bar-track">
      <div className="score-bar-fill" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

function PillarCard({ pa }) {
  return (
    <div className="analysis-pillar-card">
      <div className="analysis-pillar-header">
        <span className="pillar-badge">{PILLAR_LABELS[pa.pillar] || pa.pillar}</span>
        <span className="analysis-avg-score">
          avg&nbsp;<strong>{Number(pa.avg_score || 0).toFixed(0)}</strong>/100
        </span>
        <span className="muted" style={{ fontSize: '0.75rem' }}>
          {pa.period_start} – {pa.period_end}
        </span>
      </div>

      <ScoreBar score={Number(pa.avg_score)} />

      {pa.top_hook_patterns?.length > 0 && (
        <div className="analysis-section">
          <span className="analysis-section-label">What's working</span>
          <ul className="analysis-list analysis-list-green">
            {pa.top_hook_patterns.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}

      {pa.avoid_patterns?.length > 0 && (
        <div className="analysis-section">
          <span className="analysis-section-label">Avoid</span>
          <ul className="analysis-list analysis-list-red">
            {pa.avoid_patterns.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}

      {pa.recommended_cadence_per_week != null && (
        <p className="muted" style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
          Recommended cadence: <strong>{pa.recommended_cadence_per_week}×/week</strong>
        </p>
      )}
    </div>
  );
}

function PostRow({ post }) {
  const v = VERDICT_STYLE[post.verdict] || { color: 'var(--color-text-faint)', label: post.verdict };
  return (
    <tr>
      <td>{PILLAR_LABELS[post.pillar] || post.pillar}</td>
      <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {post.title || '—'}
      </td>
      <td style={{ color: v.color, fontWeight: 600 }}>{v.label}</td>
      <td style={{ textAlign: 'right' }}>{post.score ?? '—'}</td>
      <td className="muted" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
        {post.analyzed_at ? formatDistanceToNow(new Date(post.analyzed_at), { addSuffix: true }) : '—'}
      </td>
    </tr>
  );
}

export default function Analysis() {
  const { key } = useParams();

  const q = useQuery({
    queryKey: ['analysis', key],
    queryFn: ({ signal }) => apiGet(`/projects/${key}/analysis`, { signal }),
  });

  const pillarAnalysis = q.data?.pillar_analysis || [];
  const recentPosts    = q.data?.recent_posts || [];

  return (
    <div className="page analysis-page">
      <header className="page-header">
        <div>
          <h1>Content Analysis</h1>
          <p className="muted">
            Per-pillar performance patterns from the last 30 days.
            Runs automatically every Sunday.
          </p>
        </div>
        <Link to={`/projects/${key}`} className="btn btn-ghost">
          Back to Project
        </Link>
      </header>

      {q.isLoading && <div className="loading-state">Loading analysis…</div>}
      {q.isError && (
        <div className="error-state">Failed to load: {q.error?.message}</div>
      )}

      {!q.isLoading && !q.isError && pillarAnalysis.length === 0 && (
        <div className="empty-state">
          <p>No analysis yet.</p>
          <p className="muted">
            The analyzer runs weekly (Sundays at 15:00 UTC) once posts have been
            published and engagement stats have been collected.
          </p>
        </div>
      )}

      {pillarAnalysis.length > 0 && (
        <>
          <section>
            <h2 className="section-title">Pillar Score Cards</h2>
            <div className="analysis-pillar-grid">
              {pillarAnalysis.map((pa) => (
                <PillarCard key={pa.pillar} pa={pa} />
              ))}
            </div>
          </section>

          {recentPosts.length > 0 && (
            <section style={{ marginTop: '2rem' }}>
              <h2 className="section-title">Recent Post Scores</h2>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Pillar</th>
                      <th>Post</th>
                      <th>Verdict</th>
                      <th style={{ textAlign: 'right' }}>Score</th>
                      <th>Analyzed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPosts.map((p, i) => <PostRow key={i} post={p} />)}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
